import { isAnimal } from '@platform/schema/cast';
import { moduleCastSchema } from '@platform/schema/curriculum';

import type { CastId, CastReadiness } from '@platform/schema/cast';
import type { LessonCast } from '@platform/schema/content';
import type { CastAssignment } from '@platform/schema/curriculum';

/** A module's cast and each lesson's subset of it, as the author is editing them. */
export interface CastDraft {
  cast: CastAssignment[];
  /** Assignment ids by Lesson ID, for every lesson in the module. */
  subsets: Record<string, string[]>;
}

export const READINESS_LABEL: Record<CastReadiness, string> = {
  ready: 'Ready to animate',
  limited: 'Limited animation',
  pending: 'Artwork pending',
};

export const MODULE_CAST_CAP = 3;

const blank = { role: '', goal: '', constraint: '', relationship: '' };

function add(
  draft: CastDraft,
  one: Pick<CastAssignment, 'id' | 'character' | 'name'>,
  person: boolean,
): CastDraft {
  const lead = person && !draft.cast.some((held) => held.part === 'lead');
  return {
    ...draft,
    cast: [
      ...draft.cast,
      { ...one, ...blank, continuity: [], part: lead ? 'lead' : 'supporting' },
    ],
  };
}

/** The character's id is its assignment id: one role per module, so it never clashes. */
export const withCharacter = (draft: CastDraft, character: CastId): CastDraft =>
  add(draft, { id: character, character }, !isAnimal(character));

export const oneOff = (draft: CastDraft, name: string): CastDraft =>
  add(draft, { id: `one-off-${crypto.randomUUID()}`, name }, true);

export const setLead = (draft: CastDraft, id: string): CastDraft => ({
  ...draft,
  cast: draft.cast.map((one) => ({
    ...one,
    part: one.id === id ? 'lead' : 'supporting',
  })),
});

export const editRole = (
  draft: CastDraft,
  id: string,
  role: string,
): CastDraft => ({
  ...draft,
  cast: draft.cast.map((one) => (one.id === id ? { ...one, role } : one)),
});

/** A lesson cannot keep using someone the module no longer casts. */
export const removeAssignment = (draft: CastDraft, id: string): CastDraft => ({
  cast: draft.cast.filter((one) => one.id !== id),
  subsets: Object.fromEntries(
    Object.entries(draft.subsets).map(([lessonId, ids]) => [
      lessonId,
      ids.filter((held) => held !== id),
    ]),
  ),
});

/** Kept in cast order, so ticking in a different order is not a change. */
export function toggleInLesson(
  draft: CastDraft,
  lessonId: string,
  id: string,
): CastDraft {
  const held = new Set(draft.subsets[lessonId] ?? []);
  if (held.has(id)) held.delete(id);
  else held.add(id);
  return {
    ...draft,
    subsets: {
      ...draft.subsets,
      [lessonId]: draft.cast
        .map((one) => one.id)
        .filter((one) => held.has(one)),
    },
  };
}

/** The SDK types an optional key as absent, never as undefined. */
export const castBody = (cast: readonly CastAssignment[]) =>
  cast.map(({ character, name, ...one }) => ({
    ...one,
    ...(character !== undefined && { character }),
    ...(name !== undefined && { name }),
  }));

/** The first thing a save would be refused for, before sending it. */
export function castProblem(cast: CastAssignment[]): string | null {
  const parsed = moduleCastSchema.safeParse(cast);
  if (parsed.success) return null;
  const [issue] = parsed.error.issues;
  const field = issue?.path.at(-1);
  return field === 'role'
    ? 'Give everyone a role in this module.'
    : (issue?.message ?? 'This cast cannot be saved.');
}

export type LessonState =
  'Review changes' | 'Missing story' | 'Cast not chosen' | 'Ready to build';

export function lessonState(
  lesson: { storyBeat: string; storyChanged: boolean },
  subset: readonly string[],
): LessonState {
  if (lesson.storyChanged) return 'Review changes';
  if (lesson.storyBeat.trim() === '') return 'Missing story';
  if (subset.length === 0) return 'Cast not chosen';
  return 'Ready to build';
}

export interface Adoption {
  draft: CastDraft;
  /** Characters built in more than one role: the role adopted first, then the others. */
  conflicts: { id: CastId; roles: string[] }[];
  /** Built, but past the cap, so not adopted. */
  left: CastId[];
}

/**
 * A cast from what built lessons already name: the characters in the most lessons, each
 * in the role it was built in most often. The author settles the rest.
 */
export function adoptBuilt(
  lessons: readonly { lessonId: string; built: LessonCast }[],
): Adoption {
  const roles = new Map<CastId, Map<string, number>>();
  for (const { character, role } of lessons.flatMap((one) => one.built)) {
    const counts = roles.get(character) ?? new Map<string, number>();
    counts.set(role, (counts.get(role) ?? 0) + 1);
    roles.set(character, counts);
  }
  const total = (counts: Map<string, number>): number =>
    [...counts.values()].reduce((sum, n) => sum + n, 0);
  const ranked = [...roles.entries()]
    .map(([id, counts]) => ({
      id,
      count: total(counts),
      roles: [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([role]) => role),
    }))
    .sort((a, b) => b.count - a.count);
  const kept = ranked.slice(0, MODULE_CAST_CAP);

  let draft: CastDraft = { cast: [], subsets: {} };
  for (const one of kept) {
    draft = withCharacter(draft, one.id);
    draft = editRole(draft, one.id, one.roles[0] ?? '');
  }
  for (const { lessonId, built } of lessons) {
    const used = new Set(built.map((one) => one.character));
    draft.subsets[lessonId] = kept
      .map((one) => one.id)
      .filter((one) => used.has(one));
  }

  return {
    draft,
    conflicts: kept
      .filter((one) => one.roles.length > 1)
      .map(({ id, roles: all }) => ({ id, roles: all })),
    left: ranked.slice(MODULE_CAST_CAP).map((one) => one.id),
  };
}
